/**
 * LandingScreen — bright, SaaS-feel marketing surface. White + navy text +
 * blue accent. NOT the dark-terminal theme (operator feedback 2026-05-26
 * called the dark variant "generic AI template / scammer page").
 *
 * Two stages:
 *   stage = "landing"   → hero + features + CTA
 *   stage = "auth"      → embeds <SignInScreen /> full-screen
 */

import { useState } from "react";
import { SignInScreen } from "./SignInScreen";
import { ArrowRight, ShieldCheck, Briefcase, Activity } from "lucide-react";

// Brand palette — locked to the office-executive style ("clean white + blue").
const COLORS = {
  bg: "#ffffff",
  surface: "#f8fafc",
  surfaceAlt: "#f1f5f9",
  text: "#0f172a",
  textMuted: "#475569",
  textSubtle: "#94a3b8",
  border: "#e2e8f0",
  accent: "#2563eb",
  accentSoft: "#3b82f6",
  accentBg: "#eff6ff",
};

interface Props {
  themeId?: string;
}

type Stage = "landing" | "auth";

export function LandingScreen({ themeId }: Props) {
  const [stage, setStage] = useState<Stage>("landing");

  if (stage === "auth") {
    // SignInScreen reads its own theme; we force the light kit theme.
    return <SignInScreen themeId={themeId || "office_executive"} />;
  }

  return (
    <div
      style={{
        backgroundColor: COLORS.bg,
        color: COLORS.text,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
      className="min-h-screen w-full"
    >
      {/* Top nav */}
      <header
        style={{ borderColor: COLORS.border }}
        className="border-b"
      >
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img
              src="/icon-192.png"
              alt="Margo"
              className="h-8 w-8 rounded-lg"
            />
            <span className="font-semibold tracking-tight text-sm">
              Margo
            </span>
          </div>
          <button
            onClick={() => setStage("auth")}
            style={{ color: COLORS.text }}
            className="text-sm font-medium hover:opacity-70 transition-opacity"
          >
            Sign in
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 pt-12 sm:pt-16 pb-16">
        <div className="max-w-3xl mx-auto text-center">
          <img
            src="/icon-512.png"
            alt="Margo"
            className="mx-auto mb-8 h-24 w-24 sm:h-28 sm:w-28 rounded-2xl shadow-xl"
            style={{ boxShadow: "0 25px 50px -12px rgba(37, 99, 235, 0.35)" }}
          />

          <div
            style={{
              backgroundColor: COLORS.accentBg,
              color: COLORS.accent,
            }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1 mb-6"
          >
            <span
              style={{ backgroundColor: COLORS.accent }}
              className="h-1.5 w-1.5 rounded-full"
            />
            Private · Always on
          </div>

          <h1
            style={{ color: COLORS.text, letterSpacing: "-0.03em" }}
            className="text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.05] mb-6"
          >
            Your AI CEO,
            <br />
            <span style={{ color: COLORS.accent }}>built for your business.</span>
          </h1>

          <p
            style={{ color: COLORS.textMuted }}
            className="text-base sm:text-lg max-w-2xl mx-auto leading-relaxed mb-10"
          >
            A private agent that holds your strategy, runs your ops, and keeps
            you accountable. You execute on the ground. Your CEO holds the long
            view. Always on. Never shared.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => setStage("auth")}
              style={{
                backgroundColor: COLORS.accent,
                color: "#ffffff",
                boxShadow: "0 10px 30px -10px rgba(37, 99, 235, 0.5)",
              }}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold hover:-translate-y-0.5 transition-all duration-200"
            >
              Get started
              <ArrowRight size={16} />
            </button>
            <button
              onClick={() => setStage("auth")}
              style={{ color: COLORS.text, borderColor: COLORS.border }}
              className="inline-flex items-center px-6 py-3 rounded-lg text-sm font-semibold border hover:bg-slate-50 transition-colors"
            >
              Sign in
            </button>
          </div>

          <p
            style={{ color: COLORS.textSubtle }}
            className="text-xs mt-5"
          >
            No credit card required for the trial.
          </p>
        </div>
      </section>

      {/* Feature grid */}
      <section
        style={{ backgroundColor: COLORS.surface, borderColor: COLORS.border }}
        className="border-t border-b px-6 py-16"
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2
              style={{ color: COLORS.text, letterSpacing: "-0.02em" }}
              className="text-2xl sm:text-3xl font-bold mb-3"
            >
              Built for operators, not assistants.
            </h2>
            <p
              style={{ color: COLORS.textMuted }}
              className="text-sm sm:text-base max-w-xl mx-auto"
            >
              Three guarantees that make the difference.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FeatureCard
              icon={<ShieldCheck size={20} />}
              title="Strictly personal"
              body="Your agent serves only you. Conversations, files, and memory never cross to other users. Team chat is a separate human-only surface."
            />
            <FeatureCard
              icon={<Briefcase size={20} />}
              title="CEO, not assistant"
              body="Holds the long arc of your business. Surfaces priorities, names tradeoffs, runs the math. You execute on the ground."
            />
            <FeatureCard
              icon={<Activity size={20} />}
              title="Always on"
              body="Runs 24/7 on dedicated infrastructure. Wakes up to a logged-in state every time. Phone or desktop, same agent."
            />
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="px-6 py-16">
        <div className="max-w-2xl mx-auto text-center">
          <h2
            style={{ color: COLORS.text, letterSpacing: "-0.02em" }}
            className="text-2xl sm:text-3xl font-bold mb-4"
          >
            Ready to meet your CEO?
          </h2>
          <p
            style={{ color: COLORS.textMuted }}
            className="text-sm sm:text-base mb-8"
          >
            Sign up takes 30 seconds. Your agent is waiting.
          </p>
          <button
            onClick={() => setStage("auth")}
            style={{
              backgroundColor: COLORS.accent,
              color: "#ffffff",
              boxShadow: "0 10px 30px -10px rgba(37, 99, 235, 0.5)",
            }}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold hover:-translate-y-0.5 transition-all duration-200"
          >
            Get started
            <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer
        style={{ borderColor: COLORS.border, color: COLORS.textSubtle }}
        className="border-t px-6 py-8"
      >
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <div>© 2026 Agentic Operator Command Center</div>
          <div>Built on the dock-template chassis</div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div
      style={{
        backgroundColor: COLORS.bg,
        borderColor: COLORS.border,
      }}
      className="border rounded-xl p-6 hover:-translate-y-0.5 transition-transform duration-200"
    >
      <div
        style={{
          color: COLORS.accent,
          backgroundColor: COLORS.accentBg,
        }}
        className="inline-flex h-10 w-10 items-center justify-center rounded-lg mb-4"
      >
        {icon}
      </div>
      <h3
        style={{ color: COLORS.text }}
        className="text-base font-semibold mb-2"
      >
        {title}
      </h3>
      <p
        style={{ color: COLORS.textMuted }}
        className="text-sm leading-relaxed"
      >
        {body}
      </p>
    </div>
  );
}
